import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './form.css';
import './theme.css';
import { fetchUserField } from './utils/userSettings';

import GlobalDialog from './components/GlobalDialog';
import Footer from './components/Footer';
import Cookies from 'js-cookie'; // Import js-cookie
import { useIdleLogout } from './utils/useIdleLogout'; // [VP-IDLE-LOGOUT 2026-05-27]
import { CompanyProvider } from './contexts/CompanyContext';


import Navigation from './navigation';
import Home from './home';
import Quotes from './quotes';
import Archive from './archive';
import Orders from './orders';
import Production from './production';
import Warehouse from './warehouse';
import PFAPsetup from './Setup';
import Statistics from './Statistics';
import Templates from './templates/templates';
import SettingsConfiguration from './SettingsConfig';
import Customers from './customers';
import Dealers from './Dealers';
import Suppliers from './suppliers';
import Inventory from './inventory';
import Materials from './materials';
import Prices from './prices';
import Reports from './reports';
import ProductionData from './productiondata';
import AddiItem from './additem';
import EntryDoor from './entrydoor';
import EntryDoorEticom from './entrydooreticom';
import PleatedScreen from './pleatedscreen';
import PleatedScreenN from './pleatedscreenn';
import PatioDoor from './patiodoor';
import EntryDoorG from './entrydoorg';

import W1 from './windows/wint/w1';
import W2 from './windows/wint/w2';
import W3 from './windows/wint/w3';
import W4 from './windows/wint/w4';
import W5 from './windows/wint/w5';
import W6 from './windows/wint/w6';
import W7 from './windows/wint/w7';
import W8 from './windows/wint/w8';
import W9 from './windows/wint/w9';
import W10 from './windows/wint/w10';
import W11 from './windows/wint/w11';
import W12 from './windows/wint/w12';
import W13 from './windows/wint/w13';
import W14 from './windows/wint/w14';
import W15 from './windows/wint/w15';
import W16 from './windows/wint/w16';
import W17 from './windows/wint/w17';
import W18 from './windows/wint/w18';
import W19 from './windows/wint/w19';
import W20 from './windows/wint/w20';

import PD1 from './patiodoors/pdt/pd1';
import PD2 from './patiodoors/pdt/pd2';
import PD3 from './patiodoors/pdt/pd3';

import SD1 from './stdoors/sdt/sd1';

import Login from './auth/Login';
import Unauthorized from './auth/Unauthorized';

import { CrmSettingsProvider } from './CRM/SimpleCRM/CrmSettingsContext';
import { FeaturesProvider } from './utils/FeaturesContext';
import { AppFlagsProvider } from './utils/AppFlagsContext';
const Pipeline = React.lazy(() => import('./CRM/SimpleCRM/views/Pipeline'));
const CustomerHistory = React.lazy(() => import('./CRM/SimpleCRM/views/CustomerHistory'));
const CrmDashboard = React.lazy(() => import('./CRM/SimpleCRM/views/CrmDashboard'));
const CrmAdmin = React.lazy(() => import('./CRM/SimpleCRM/views/CrmAdmin'));
const Leads = React.lazy(() => import('./CRM/SimpleCRM/views/Leads'));



const App = () => {
  // [VP-IDLE-LOGOUT 2026-05-27] auto-logout after IDLE_TIMEOUT_MS of inactivity
  // (see utils/useIdleLogout.js for the tunable and activity-event list).
  useIdleLogout();

  const [isNavCollapsed, setIsNavCollapsed] = useState(false);

  // High-DPI compensation: keep the app rendered at "100% effective scale"
  // regardless of Windows display scaling (100%, 125%, 150%, etc.)
  //
  // Strategy:
  //   - At 100% DPI (devicePixelRatio = 1): do nothing.
  //   - At >100% DPI: apply transform: scale(1/ratio) to body so the UI is
  //     scaled DOWN to compensate for the OS scaling UP.
  //     Body width/height set to ratio*100% so the scaled box still fills viewport.
  //
  // Side-effect: body becomes the containing block for position: fixed,
  // and viewport-unit CSS (100vw, 100vh, 100dvh) gets visually shrunk by 1/ratio.
  // Other components use the --ui-scale CSS variable to multiply viewport units
  // back up so they render at the expected visible size.
  useEffect(() => {
      // macOS handles HiDPI natively (Retina reports ratio=2 but the OS doesn't
      // scale the UI up), so the Windows-style compensation below would shrink
      // the page to half size. Use a dedicated Mac branch and leave the
      // Windows/Ubuntu path untouched.
      const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);
      if (isMac) {
          document.documentElement.style.setProperty('--ui-scale', 1);
          return;
      }

      const ratio = window.devicePixelRatio || 1;
      document.documentElement.style.setProperty('--ui-scale', ratio);
      if (ratio > 1) {
          const zoom = 1 / ratio;
          document.body.style.transform = `scale(${zoom})`;
          document.body.style.transformOrigin = "top left";
          document.body.style.width = `${100 * ratio}%`;
          document.body.style.height = `${100 * ratio}vh`;
      }
  }, []);

  // Apply saved theme on every page load/refresh
  useEffect(() => {
      if (Cookies.get('access_token')) {
          fetchUserField('theme', (val) => {
              document.body.setAttribute('data-theme', val || 'default');
          });
      }
  }, []);

  const [auth, setAuth] = useState(() => {
    // Load user pages from localStorage
    let userPages = [];
    try {
      const storedPages = localStorage.getItem('user_role_pages');
      if (storedPages) {
        userPages = JSON.parse(storedPages);
      }
    } catch (e) {
      console.error('Error parsing user_role_pages:', e);
    }

    return {
      isAuthenticated: !!Cookies.get('access_token'),
      initialized: true,
      csrf_token: Cookies.get('csrf_token'),
      userRole: Cookies.get('user_role'),
      userId: Cookies.get('user_id'),
      userEmail: Cookies.get('user_email'),
      userPages: userPages,
    };
  });

  const handleLogin = (authData) => {
    // ✅ Store user role & ID but NOT access_token (handled by backend)
    Cookies.set('csrf_token', authData.csrf_token);
    Cookies.set('user_role', authData.role);
    Cookies.set('user_id', authData.id);
    Cookies.set('user_email', authData.email);

    // Get pages from localStorage (set by Login.js)
    let userPages = [];
    try {
      const storedPages = localStorage.getItem('user_role_pages');
      if (storedPages) {
        userPages = JSON.parse(storedPages);
      }
    } catch (e) {
      console.error('Error parsing user_role_pages:', e);
    }

    setAuth({
      isAuthenticated: true,
      initialized: true,
      csrf_token: authData.csrf_token,
      userRole: authData.role,
      userId: authData.id,
      userEmail: authData.email,
      userPages: userPages,
    });

    return true;
  };

  const handleLogout = () => {
    // ✅ Remove user session cookies and localStorage items
    localStorage.removeItem("csrf_token");
    localStorage.removeItem("user");
    localStorage.removeItem("user_role_pages");
    localStorage.removeItem("user_role_label");
    Cookies.remove('access_token');
    Cookies.remove('csrf_token');
    Cookies.remove('crm_role');
    Cookies.remove('user_role');
    Cookies.remove('user_id');
    Cookies.remove('user_email');
    Cookies.remove('crm_user_id');

    setAuth({
      isAuthenticated: false,
      initialized: true,
      userRole: null,
      userPages: [],
    });
  };

  useEffect(() => {
    if (!auth.initialized) {
      setAuth((prev) => ({ ...prev, initialized: true }));
    }
  }, [auth.initialized]);

  useEffect(() => {
    window.addEventListener("logout", handleLogout);
    return () => window.removeEventListener("logout", handleLogout);
  }, [handleLogout]);

  // Proactively detect cookie expiry without needing a 401
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    const interval = setInterval(() => {
      if (!Cookies.get('access_token')) {
        handleLogout();
      }
    }, 60000); // check every 60 seconds
    return () => clearInterval(interval);
  }, [auth.isAuthenticated]);

  // Map route paths to page IDs for dynamic access control
  const PAGE_ID_MAP = {
    '/home': 'home',
    '/quotes': 'quotes',
    '/archive': 'quotes', // Archive is part of quotes
    '/customers': 'customers',
    '/orders': 'orders',
    '/production': 'production',
    '/warehouse': 'warehouse',
    '/setup': 'setup',
    '/statistics': 'statistics',
    '/templates': 'templates',
    '/settings': 'settings',
    '/suppliers': 'warehouse',
    '/inventory': 'warehouse',
    '/materials': 'warehouse',
    '/prices': 'setup',
    '/reports': 'statistics',
    '/productiondata': 'production',
    '/pipeline': 'quotes',
    '/customer-history': 'customers',
    '/crm-dashboard': 'quotes',
    '/crm-admin': 'crm-admin',
    '/leads': 'quotes',
    '/additem': 'quotes',
    '/entrydoor': 'quotes',
    '/entrydooreticom': 'quotes',
    '/patiodoor': 'quotes',
    '/pleatedscreen': 'quotes',
    '/pleatedscreenn': 'quotes',
    '/entrydoorg': 'quotes',
  };

  const ProtectedRoute = ({ children, allowedRoles, pageId, requiresCrmRole }) => {
    if (!auth.initialized) {
      return <div>Loading...</div>;
    }

    if (!auth.isAuthenticated) {
      return <Navigate to="/Login" replace />;
    }

    if (requiresCrmRole && !Cookies.get('crm_role')) {
      return <Navigate to="/unauthorized" replace />;
    }

    // Use dynamic page-based access if userPages is available
    if (auth.userPages && auth.userPages.length > 0) {
      // Determine the page ID - either passed explicitly or derived from path
      const currentPath = window.location.pathname.toLowerCase();
      const effectivePageId = pageId || PAGE_ID_MAP[currentPath];

      // Windows routes map to 'quotes' page
      if (currentPath.startsWith('/windows/')) {
        if (!auth.userPages.includes('quotes')) {
          return <Navigate to="/unauthorized" replace />;
        }
        return children;
      }

      // Patio doors routes map to 'quotes' page
      if (currentPath.startsWith('/patiodoors/')) {
        if (!auth.userPages.includes('quotes')) {
          return <Navigate to="/unauthorized" replace />;
        }
        return children;
      }

      // If we have a page ID, check if user has access
      if (effectivePageId && !auth.userPages.includes(effectivePageId)) {
        return <Navigate to="/unauthorized" replace />;
      }
    } else if (allowedRoles && !allowedRoles.includes(auth.userRole)) {
      // Fallback to role-based access if no pages defined
      return <Navigate to="/unauthorized" replace />;
    }

    return children;
  };


  const PublicRoute = ({ children }) => {
    return auth.isAuthenticated ? <Navigate to="/home" replace /> : children;
  };

  // Memoize routes to prevent re-render when only navigation collapsed state changes
  const routesContent = useMemo(() => (
    <Routes>
            {/* Public Route - only accessible when not logged in */}
             {/* Public routes */}
            <Route path="/Login" element={<PublicRoute><Login onLogin={handleLogin} /></PublicRoute>} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Protected Routes */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            
            <Route path="/home" element={ <ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/quotes"  element={<ProtectedRoute allowedRoles={['superadmin', 'admin', 'sales', 'sales windows', 'sales windoors', 'manager']}><Quotes /></ProtectedRoute>}/>
            <Route path="/archive" element={<ProtectedRoute allowedRoles={['superadmin','admin', 'sales', 'sales windows', 'sales windoors']} ><Archive /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute allowedRoles={['superadmin','admin']} ><Orders /></ProtectedRoute>} />
            <Route path="/production" element={<ProtectedRoute allowedRoles={['superadmin','admin1']} ><Production /></ProtectedRoute>} />
            <Route path="/warehouse" element={<ProtectedRoute allowedRoles={['superadmin','admin1']} ><Warehouse /></ProtectedRoute>} />
            <Route path="/Setup" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'manager']} ><PFAPsetup /></ProtectedRoute>} />
            <Route path="/Statistics" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'manager']} ><Statistics /></ProtectedRoute>} />
            <Route path="/Templates" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'manager']} ><Templates /></ProtectedRoute>} />
            <Route path="/Settings" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'manager']} ><SettingsConfiguration userRole={auth.userRole} /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute allowedRoles={['superadmin','admin', 'sales', 'sales windows', 'manager']} ><Customers /></ProtectedRoute>} />
            <Route path="/dealers" element={<ProtectedRoute allowedRoles={['superadmin','admin','owner']}><Dealers /></ProtectedRoute>} />
            <Route path="/suppliers" element={<ProtectedRoute allowedRoles={['superadmin','admin1']}><Suppliers /></ProtectedRoute>} />

            <Route path="/inventory" element={<ProtectedRoute allowedRoles={['superadmin','admin1']} ><Inventory /></ProtectedRoute>} />
            <Route path="/materials" element={<ProtectedRoute allowedRoles={['superadmin','admin1']}><Materials /></ProtectedRoute>} />
            <Route path="/prices" element={<ProtectedRoute    allowedRoles={['superadmin','admin1']}><Prices /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute   allowedRoles={['superadmin','admin1']}><Reports /></ProtectedRoute>} />
            <Route path="/productiondata" element={<ProtectedRoute allowedRoles={['superadmin','admin1']}><ProductionData /></ProtectedRoute>} />

            <Route path="/additem" element={<ProtectedRoute allowedRoles={['superadmin','admin', 'sales', 'sales windows', 'sales windoors']}><AddiItem /></ProtectedRoute>} />
            <Route path="/entrydoor" element={<ProtectedRoute allowedRoles={['superadmin','admin', 'sales', 'sales windows', 'sales windoors']}><EntryDoor /></ProtectedRoute>} />
            <Route path="/entrydooreticom" element={<ProtectedRoute allowedRoles={['superadmin','admin', 'sales', 'sales windows', 'sales windoors']}><EntryDoorEticom /></ProtectedRoute>} />
            <Route path="/patiodoor" element={<ProtectedRoute allowedRoles={['superadmin','admin', 'sales', 'sales windows', 'sales windoors']}><PatioDoor /></ProtectedRoute>} />
            <Route path="/pleatedscreen" element={<ProtectedRoute allowedRoles={['superadmin','admin', 'sales', 'sales windows', 'sales windoors']}><PleatedScreen /></ProtectedRoute>} />
            <Route path="/stdoors/sdt/sd1" element={<ProtectedRoute allowedRoles={['superadmin','admin', 'sales', 'sales windows', 'sales windoors']}><SD1 /></ProtectedRoute>} />
            <Route path="/entrydoorg" element={<ProtectedRoute ><EntryDoorG allowedRoles={['admin']}/></ProtectedRoute>} />
            <Route path="/windows/wint/w1"  element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W1 /></ProtectedRoute>} />
            <Route path="/windows/wint/w2"  element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W2 /></ProtectedRoute>} />
            <Route path="/windows/wint/w3"  element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W3 /></ProtectedRoute>} />
            <Route path="/windows/wint/w4"  element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W4 /></ProtectedRoute>} />
            <Route path="/windows/wint/w5"  element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W5 /></ProtectedRoute>} />
            <Route path="/windows/wint/w6"  element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W6 /></ProtectedRoute>} />
            <Route path="/windows/wint/w7"  element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W7 /></ProtectedRoute>} />
            <Route path="/windows/wint/w8"  element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W8 /></ProtectedRoute>} />
            <Route path="/windows/wint/w9"  element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W9 /></ProtectedRoute>} />
            <Route path="/windows/wint/w10" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W10 /></ProtectedRoute>} />
            <Route path="/windows/wint/w11" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W11 /></ProtectedRoute>} />
            <Route path="/windows/wint/w12" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W12 /></ProtectedRoute>} />
            <Route path="/windows/wint/w13" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W13 /></ProtectedRoute>} />
            <Route path="/windows/wint/w14" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W14 /></ProtectedRoute>} />
            <Route path="/windows/wint/w15" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W15 /></ProtectedRoute>} />
            <Route path="/windows/wint/w16" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W16 /></ProtectedRoute>} />
            <Route path="/windows/wint/w17" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W17 /></ProtectedRoute>} />
            <Route path="/windows/wint/w18" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W18 /></ProtectedRoute>} />
            <Route path="/windows/wint/w19" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W19 /></ProtectedRoute>} />
            <Route path="/windows/wint/w20" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><W20 /></ProtectedRoute>} />

            {/* Patio Doors Routes */}
            <Route path="/pleatedscreenn" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><PleatedScreenN /></ProtectedRoute>} />
            <Route path="/patiodoors/pdt/pd1"element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><PD1 /></ProtectedRoute>} />
            <Route path="/patiodoors/pdt/pd2" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><PD2 /></ProtectedRoute>} />
            <Route path="/patiodoors/pdt/pd3" element={<ProtectedRoute allowedRoles={['superadmin','admin1', 'sales windows', 'sales windoors', 'manager']}><PD3 /></ProtectedRoute>} />

            {/* Add all your other routes similarly */}
            {/* ... */}

            {/* Simple CRM Routes — always registered; access controlled by requiresCrmRole */}
            <Route path="/pipeline" element={<ProtectedRoute requiresCrmRole><Suspense fallback={<div>Loading...</div>}><Pipeline /></Suspense></ProtectedRoute>} />
            <Route path="/customer-history/:id" element={<ProtectedRoute requiresCrmRole><Suspense fallback={<div>Loading...</div>}><CustomerHistory /></Suspense></ProtectedRoute>} />
            <Route path="/crm-dashboard" element={<ProtectedRoute requiresCrmRole><Suspense fallback={<div>Loading...</div>}><CrmDashboard /></Suspense></ProtectedRoute>} />
            <Route path="/crm-admin" element={<ProtectedRoute allowedRoles={['superadmin','admin']}><Suspense fallback={<div>Loading...</div>}><CrmAdmin /></Suspense></ProtectedRoute>} />
            <Route path="/leads" element={<ProtectedRoute requiresCrmRole><Suspense fallback={<div>Loading...</div>}><Leads /></Suspense></ProtectedRoute>} />

            {/* Catch-all route */}
            <Route path="*" element={<Navigate to={auth.isAuthenticated ? "/home" : "/Login"} />} />
          </Routes>
  ), [auth.isAuthenticated, auth.userPages]); // Re-render routes when auth or pages change

  return (
    <>
    <GlobalDialog />
    <Router>
      <AppFlagsProvider>
      <CrmSettingsProvider key={`crm-${auth.isAuthenticated}-${auth.userRole}`}>
      <FeaturesProvider key={auth.userRole}>
      <CompanyProvider>
        <div className={`app-container ${isNavCollapsed ? 'nav-collapsed' : ''}`}>
          <Navigation
            onLogout={handleLogout}
            userRole={auth.userRole}
            userPages={auth.userPages}
            isAuthenticated={auth.isAuthenticated}
            onToggleCollapse={setIsNavCollapsed}
            isCollapsed={isNavCollapsed}
          />
          <div className="content-container">
            {routesContent}
        </div>
        <Footer />
      </div>
      </CompanyProvider>
      </FeaturesProvider>
      </CrmSettingsProvider>
      </AppFlagsProvider>
    </Router>
    </>
  );
};

export default App;